import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserPreferences {
  id: string;
  user_id: string;
  theme: string;
  language: string;
  email_notifications: boolean;
  push_notifications: boolean;
  transaction_alerts: boolean;
  budget_alerts: boolean;
  exchange_rate_alerts: boolean;
  exchange_rate_threshold: number | null;
  created_at: string;
  updated_at: string;
}

export function useUserPreferences() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['user_preferences', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user!.id)
        .single();
      if (error) {
        // If no preferences exist yet, create them
        if (error.code === 'PGRST116') {
          const { data: newData, error: insertError } = await supabase
            .from('user_preferences')
            .insert({ user_id: user!.id })
            .select()
            .single();
          if (insertError) throw insertError;
          return newData as UserPreferences;
        }
        throw error;
      }
      return data as UserPreferences;
    },
    enabled: !!user,
  });
}

export function useUpdateUserPreferences() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (updates: Partial<Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>>) => {
      const { data, error } = await supabase
        .from('user_preferences')
        .update(updates)
        .eq('user_id', user!.id)
        .select()
        .single();
      if (error) throw error;
      return data as UserPreferences;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user_preferences'] }),
  });
}
